import { Copy, IdCardIcon, Mail, Phone, User, UserCircle, UserCircle2, UserCircleIcon, UserIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useRoute from '../hooks/routesHook';
import { getPhoneNumberFromShortCodeFormat, sentenceCase, showToast } from '../utils/miscFunctions';
import RedactInfo from './elements/RedactInfo';
import { CUSTOMER_TYPE, REDACT_INFO_TYPES } from '../resources/enums';
import IconStatus from './elements/IconStatus';
import ToolTip from './ToolTip';
import useUserAccount from '../hooks/userAccountHook';
import CustomerEmailVerificationModal from '../views/shared/policies/auto/CustomerEmailVerificationModal';
import useModal from '../hooks/modalHook';
import CustomerPhoneVerificationModal from '../views/shared/policies/auto/PhoneVerificationModal'
import { useQueryClient } from '@tanstack/react-query';
import { runKycAnalysisOnCustomer, verifyCustomerID } from '../api/requests';
import { Policy } from '../models/policy/Policy';
import useAnalysis from '../hooks/analysisHook';
import { LuExternalLink } from 'react-icons/lu';
import Tooltip from './ToolTip';
import { ICustomerType } from '../resources/interfaces';

interface ICustomerInfo {
  firstName: string | undefined;
  lastName: string | undefined;
  businessName: string | undefined;
  email: string | undefined;
  phoneNumber: string | undefined;
  otherPhone: string | undefined;
  id: string | number;
  idVerified: boolean;
  idNumber?: string;
  emailVerified?: boolean;
  phoneVerified: boolean;
  score: number | undefined;
  risk?: string | boolean;
  policy?: Policy | undefined;
  isClickable?: boolean;
  type?: ICustomerType;
  query?: string | undefined;
}

function CustomerInfo({ firstName, lastName, businessName, email, phoneNumber, otherPhone, id, idVerified, idNumber, emailVerified, phoneVerified, score, risk, policy, isClickable = false, query, type }: ICustomerInfo) {
  const { getDashboardBase } = useRoute()
  const { user } = useUserAccount()
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { getKycStatusInfo, getRiskScoreInfo } = useAnalysis()
  const queryClient = useQueryClient()
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false)
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const ref = useRef(null);

  const showCustomerEmailVerificationModal = () => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <CustomerEmailVerificationModal
          policy={policy}
          close={closeModal}
          onProceed={() => {
            closeModal();
          }}
          id={Number(id)}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  const runCustomerKycAnalysis = async () => {
    try {
      if (!id) return;
      setLoading(true);
      const response: any = await runKycAnalysisOnCustomer(String(id));
      if (response) {
        showToast.success(response?.message ?? "Customer KYC analysis run successfully")
        invalidateQueries()
      }
    } catch (error: any) {
      console.error(error?.description ?? 'Error running customer KYC analysis:');
    } finally {
      setLoading(false);
    }
  };

  const invalidateQueries = () => {
    query && queryClient.invalidateQueries({ queryKey: [query] });
  }

  const showCustomerPhoneVerificationModal = (phone: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <CustomerPhoneVerificationModal
          policy={policy}
          close={closeModal}
          onProceed={() => {
            closeModal();
            window.location.reload();
          }}
          phoneNumber={phone}
          id={Number(id)}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };


  const manuallyVerifyCustomerID = async () => {
    try {
      if (id) {
        const response = await verifyCustomerID(Number(id));
        if (response) {
          window.location.reload();
          showToast.success(response.message);
        }
      }
    } catch (error: any) {
      showToast.error(error?.description ?? 'Could not verify the customer ID. Please try again.');
    }
  };

  return (
    <>

      <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border h-fit">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg-border dark:bg-transparent dark:border-darkBg-border">
          <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
            <UserIcon className="h-7 w-7 mr-2 text-gray-500 dark:text-darkBg-mainText" />
            Customer Information
          </h2>
        </div>
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
              <UserIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="ml-4 flex  flex-col gap-2">
              <div className='flex items-center gap-3'>
                <h3 className="text-lg flex gap-2 items-center font-medium text-gray-900 dark:text-darkBg-mainText">{type === CUSTOMER_TYPE.BUSINESS ? businessName : (firstName + ' ' + lastName)} </h3>

                <ToolTip message={`Click to view customer`} placement='bottom'>
                  <div className='h-[20px] w-[20px] flex items-end'>
                    <LuExternalLink onClick={() => window.open(`${getDashboardBase()}/customers/${id}`, '_blank')} className='h-4 w-4 cursor-pointer' />
                  </div>
                </ToolTip>
              </div>
              <div className="flex flex-wrap gap-[2px] mb-1">

                {/* KYC Status Badge */}
                {!idVerified ?
                  <ToolTip message={`Click to verify`} placement='bottom'>
                    <div onClick={runCustomerKycAnalysis} className={`flex items-center cursor-pointer border hover:border-red-500 transition-all w-fit py-0.5 px-2 rounded-full text-[9px] ${getKycStatusInfo(idVerified).color}`}>
                      {getKycStatusInfo(idVerified).icon}
                      <span className="ml-1 font-medium capitalize whitespace-nowrap text-[9px]">KYC: {'Not Verified'}</span>
                    </div>
                  </ToolTip> :
                  <div className={`flex items-center  w-fit py-0.5 px-2 rounded-full text-[9px] ${getKycStatusInfo(idVerified).color}`}>
                    {getKycStatusInfo(idVerified).icon}
                    <span className="ml-1 font-medium capitalize whitespace-nowrap text-[9px]">KYC: {'Verified'}</span>
                  </div>
                }




                {/* Risk Score Badge */}
                {
                  (
                    <div className={`flex items-center gap-1 py-0.5 px-2 rounded-full text-xs ${score ? getRiskScoreInfo(score).color : 'bg-red-100 text-red-800'} `}>
                      {score && getRiskScoreInfo(score).icon}
                      {/* <span className="ml-1 font-medium">Risk: {RNPLApplication?.vehicle?.score}</span> */}
                      <span className={`font-medium text-[9px]`}>Risk: {risk ? risk : '-'}</span>
                    </div>
                  )
                }
                <div className={`flex items-center  w-fit py-0.5 px-2 rounded-full text-[9px] bg-orange-100 text-orange-800`}>
                  <User className='h-4 w-4' />
                  <span className="ml-1 font-medium capitalize whitespace-nowrap text-[9px]">Type: {type ? sentenceCase(type) : '-'}</span>
                </div>
              </div>
            </div>

          </div>
          <div className="mt-5 grid grid-cols-1 gap-4">
            <div className="flex items-center">
              <div className='h-[20px] w-[20px] mr-2'><Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" /></div>


              <div className='flex items-center '>
                <span className="text-sm text-gray-900 dark:text-darkBg-mainText mr-2">{email ? email : '-'}</span>
                {email && <Tooltip
                  placement="bottom"
                  bgColor="bg-black"
                  textColor="text-white"
                  message={isCopied ? 'Copied!' : 'Copy email'}
                >
                  <Copy ref={ref} className='h-3 w-3 cursor-pointer' onMouseLeave={() => setIsCopied(false)} onClick={async () => { await navigator.clipboard.writeText(`${email}`); setIsCopied(true) }} />
                </Tooltip>}
              </div>
              {
                email && (
                  <>
                    {emailVerified ? (
                      <IconStatus status={true} />
                    ) : (
                      <ToolTip message={`Click to verify`} placement='bottom'>
                        <div>
                          <IconStatus status={emailVerified} onclick={() => isClickable && showCustomerEmailVerificationModal} cursorPointer />
                        </div>
                      </ToolTip>
                    )}
                  </>)
              }
            </div>
            <div className="flex items-center">
              <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
              <span className="flex items-center gap-x-2 text-sm text-gray-900 dark:text-darkBg-mainText">{phoneNumber && policy ? (
                <RedactInfo
                  infoType={REDACT_INFO_TYPES.PHONE}
                  info={getPhoneNumberFromShortCodeFormat(phoneNumber ?? '')}
                  dataId={policy?.ref ?? ''}
                  dataType="policy"
                  clickable={!user.isInsurer()}
                />
              ) : (
                getPhoneNumberFromShortCodeFormat(phoneNumber ?? '-')
              )}
                {phoneVerified ? (
                  <IconStatus status={true} />
                ) : (
                  <ToolTip message={`Click to verify`} placement='bottom'>
                    <div>
                      <IconStatus status={false} onclick={() => isClickable && showCustomerPhoneVerificationModal(phoneNumber ?? '')} cursorPointer />
                    </div>
                  </ToolTip>
                )}
              </span>
            </div>
            {otherPhone &&
              <div className="flex items-center">
                <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                <span className='text-sm text-gray-900 dark:text-darkBg-mainText'>{otherPhone ? getPhoneNumberFromShortCodeFormat(otherPhone) : '-'}</span>
              </div>
            }
            <div className="flex items-center">
              <IdCardIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
              <span className="flex items-center  justify-center gap-x-2 text-sm text-gray-900 dark:text-darkBg-mainText">{idNumber && policy ? (
                <RedactInfo
                  infoType={REDACT_INFO_TYPES.ID_NUMBER}
                  info={idNumber ?? ''}
                  dataId={policy?.ref ?? ''}
                  dataType="policy"
                  clickable={!user.isInsurer()}
                />
              ) : (
                idNumber ?? ''
              )}
                {idVerified ? (
                  <IconStatus status={true} />
                ) : (
                  <ToolTip message={`Click to verify`} placement='bottom'>
                    <div>
                      <IconStatus
                        cursorPointer
                        onclick={manuallyVerifyCustomerID}
                        status={idVerified}
                      />
                    </div>
                  </ToolTip>
                )}
              </span>
            </div>
          </div>

          {/* Employment & personal details captured on the policy/edit form.
              Only rendered when present so incomplete customers stay uncluttered. */}
          {(policy?.applicant?.occupation || (policy?.applicant as any)?.industry || policy?.applicant?.employerName || policy?.applicant?.employerPhoneNumber
            || policy?.applicant?.employerAddress || policy?.applicant?.monthlyIncome || policy?.applicant?.gender
            || policy?.applicant?.dateOfBirth || policy?.applicant?.address) && (
            <div className="mt-5 pt-4 border-t border-gray-200 dark:border-darkBg-border grid grid-cols-1 gap-2">
              {(policy?.applicant as any)?.industry && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Industry</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{(policy?.applicant as any)?.industry}</span></div>
              )}
              {policy?.applicant?.occupation && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Occupation</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{policy?.applicant?.occupation}</span></div>
              )}
              {policy?.applicant?.gender && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Gender</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{sentenceCase(String(policy?.applicant?.gender))}</span></div>
              )}
              {policy?.applicant?.address && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Address</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{policy?.applicant?.address}</span></div>
              )}
              {policy?.applicant?.employerName && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Employer</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{policy?.applicant?.employerName}</span></div>
              )}
              {policy?.applicant?.employerPhoneNumber && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Employer Contact</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{getPhoneNumberFromShortCodeFormat(policy?.applicant?.employerPhoneNumber)}</span></div>
              )}
              {policy?.applicant?.employerAddress && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Employer Address</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{policy?.applicant?.employerAddress}</span></div>
              )}
              {policy?.applicant?.monthlyIncome && (
                <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-darkBg-subText">Monthly Income Band</span><span className="text-gray-900 dark:text-darkBg-mainText text-right">{policy?.applicant?.monthlyIncome}</span></div>
              )}
            </div>
          )}
        </div>
      </div>

      <ModalComponent />
    </>
  )
}

export default CustomerInfo